import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ExploitationContext, LoadingContext } from 'utils/context';
import { IMPORT_FROM, IMPORT_TYPE } from 'utils/GlobalVariables';
import { STATUS_CODES, getResultStatusCode } from './importStatus.constants';
import { importMatchingParcels } from 'services/API/Cooperative';
import { displayErrorMessage, displayMessage } from 'utils/tools_functions';
import { finalizeProcedure } from 'services/API/Exploitation';
import { useTranslation } from 'react-i18next';

/**
 * Hook that handles navigation after an import
 */
export const useImportNavigation = (resetImportContext) => {
  const navigate = useNavigate();
  const { setCustomText } = useContext(LoadingContext);
  const { setExploitationContext } = useContext(ExploitationContext);
  const { t } = useTranslation();

  /**
   * Handles parcel matching
   */
  const handleMatchingData = async ({
    importType,
    jobId,
    locationRoute,
    importFrom,
    nextCampaignChosen,
  }) => {
    try {
      const importedData = await importMatchingParcels(jobId);

      if (!jobId || !importType || !locationRoute || !importedData?.data) {
        toast.error(displayErrorMessage('ERR_UNEXPECTED'));
        return;
      }

      toast.info(t('message.manualValidationRequired'));

      navigate('/exploitation/import/procedure', {
        state: {
          result: importedData?.data,
          importType,
          jobId,
          redirectTo: locationRoute,
          from: importFrom,
          nextCampaignChosen,
        },
      });
    } catch (error) {
      toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_IMPORT'));
    }
  };

  /**
   * Finalizes the procedure after a successful import
   */
  const handleFinalizeProcedure = async ({ nextCampaignChosen }) => {
    const exploitationId = localStorage.getItem('exploitation');

    try {
      const result = await finalizeProcedure(exploitationId, {
        forecast: {
          startYear: nextCampaignChosen?.startYear,
          endYear: nextCampaignChosen?.endYear,
        },
      });

      toast.success(t('success.procedureCompleted'));

      setExploitationContext((prevContext) => ({
        ...prevContext,
        ...result.data.data.exploitation,
        parcels: result.data.data.parcels,
        forecastCampaign: result.data.data.exploitation.forecastCampaign,
        possibleCampaignChange: result.data.data.exploitation.possibleCampaignChange,
      }));

      navigate('/exploitation/parcels', {
        state: {
          exploitContextReloadNeeded: false,
        },
      });
    } catch (error) {
      const errorResponse = error?.response?.data;
      if (
        errorResponse?.data?.error_type?.match('ERR_HAS_BEEN_TAKEN_OVER_ENDED') ||
        errorResponse?.data?.error_type?.match('ERR_HAS_BEEN_TAKEN_OVER_STARTED')
      ) {
        toast.error(displayErrorMessage(errorResponse.data.error_type, errorResponse.data));
        navigate('/exploitation/parcels');
      } else {
        toast.error(errorResponse?.message ?? displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  /**
   * Main navigation function after import
   */
  const navigateAfterImport = async ({
    result,
    jobId,
    importFrom,
    importType,
    importOriginLocation,
    nextCampaignChosen,
    geofoliaImportSteps,
  }) => {
    if (!result || !jobId) {
      return;
    }

    const resultStatusCode = getResultStatusCode(result);
    const exploitationId = localStorage.getItem('exploitation');

    // Special case Geofolia: step 1 → navigate to process
    if (importType === IMPORT_TYPE.geofolia && geofoliaImportSteps === 1) {
      navigate(`/${importOriginLocation}/import/geofolia/process`, {
        state: {
          jobId,
          from: importFrom,
          nextCampaignChosen,
          procedureExploitation: exploitationId,
        },
      });
      return;
    }

    // Case: cached data, requires manual matching
    if (resultStatusCode === STATUS_CODES.CACHED) {
      setCustomText(t('message.importingData', { importType }));
      await handleMatchingData({
        importType,
        jobId,
        locationRoute: importOriginLocation,
        importFrom,
        nextCampaignChosen,
      });
      return;
    }

    // Case: new exploitation
    if (resultStatusCode === STATUS_CODES.CREATED) {
      toast.success(t('success.newExploitationImported'));
      navigate(`/${importOriginLocation}/import/result`);
      return;
    }

    // Case: updated exploitation
    if (resultStatusCode === STATUS_CODES.UPDATED) {
      if (importFrom === IMPORT_FROM.classic) {
        toast.success(t('success.importSuccess'));
        navigate(`/${importOriginLocation}/import/result`);
      } else if (importFrom === IMPORT_FROM.procedure) {
        toast.success(displayMessage('IMPORT_SUCESS'));
        await handleFinalizeProcedure({ nextCampaignChosen });
        resetImportContext();
      }
      return;
    }

    // Case: failure
    if (importFrom === IMPORT_FROM.classic) {
      navigate(`/${importOriginLocation}/import/result`);
    } else if (importFrom === IMPORT_FROM.procedure) {
      const errorMessage =
        typeof result?.message === 'string'
          ? result.message
          : (resultStatusCode ?? t('common.error'));
      toast.error(errorMessage);
      resetImportContext();
    }
    return;
  };

  return { navigateAfterImport, handleMatchingData };
};
